import {
  ArrowRightLeft,
  Calendar,
  CheckCircle2,
  FileText,
  Globe,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Settings2,
  StickyNote,
  Trash2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { ActivityType } from '@buildbee/contracts';
import { crm } from '@buildbee/domain';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
  Textarea,
  cn,
  formatDate,
  formatDateTime,
  formatRelative,
  useToast,
} from '@buildbee/ui';
import { errorMessage, trpc, type RouterOutputs } from '../../../lib/trpc';
import { LoadMore, MemberSelect, useMembers } from './pickers';

type Anchor = { contactId?: string; companyId?: string; dealId?: string; projectId?: string };

const ICONS: Record<ActivityType, ReactNode> = {
  note: <StickyNote />,
  call: <Phone />,
  email: <Mail />,
  meeting: <Calendar />,
  site_visit: <MapPin />,
  sms: <MessageSquare />,
  task_completed: <CheckCircle2 />,
  stage_change: <ArrowRightLeft />,
  portal_action: <Globe />,
  document: <FileText />,
  system: <Settings2 />,
};

const MANUAL: { value: ActivityType; label: string }[] = [
  { value: 'note', label: 'Note' },
  { value: 'call', label: 'Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site visit' },
  { value: 'email', label: 'Email (logged)' },
  { value: 'sms', label: 'Text message' },
];

type ActivityItem = RouterOutputs['crm']['activities']['list']['items'][number];

function ActivityBody({ item }: { item: ActivityItem }) {
  const html = item.type === 'email' && (item.metadata as { html?: boolean }).html && item.body;
  if (html) {
    // Server-sanitized on send; sanitize again on display (defence in depth).
    return <div className="prose prose-sm mt-1 max-w-none text-sm text-muted-foreground dark:prose-invert" dangerouslySetInnerHTML={{ __html: crm.sanitizeHtml(item.body!) }} />;
  }
  return item.body ? <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{item.body}</p> : null;
}

/** Activity composer + timeline for a contact, company, deal or project. */
export function Timeline({ anchor, compact }: { anchor: Anchor; compact?: boolean }) {
  const [type, setType] = useState<ActivityType | ''>('');
  const query = trpc.crm.activities.list.useInfiniteQuery(
    { ...anchor, limit: 25, type: type || undefined },
    { getNextPageParam: (p) => p.nextCursor ?? undefined },
  );
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="space-y-4">
      <ActivityComposer anchor={anchor} />
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Activity</h3>
        <Select aria-label="Filter activity" className="h-8 w-44 text-xs" value={type} onChange={(e) => setType(e.target.value as ActivityType | '')}>
          <option value="">All activity</option>
          {[...MANUAL, { value: 'stage_change', label: 'Stage changes' }, { value: 'portal_action', label: 'Client portal' }, { value: 'task_completed', label: 'Completed tasks' }, { value: 'document', label: 'Documents' }, { value: 'system', label: 'System' }].map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>
      {query.isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="No activity yet" description="Log a call, note or meeting to start the timeline." />
      ) : (
        <ol className={cn('relative space-y-4 border-l pl-6', compact && 'space-y-3')}>
          {items.map((a) => (
            <TimelineItem key={a.id} item={a} />
          ))}
        </ol>
      )}
      <LoadMore hasMore={!!query.hasNextPage} loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()} />
    </div>
  );
}

function TimelineItem({ item }: { item: ActivityItem }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const archive = trpc.crm.activities.archive.useMutation({
    onSuccess: async () => {
      toast.success('Activity removed');
      setConfirm(false);
      await utils.crm.activities.list.invalidate();
    },
    onError: (e) => toast.error('Could not remove activity', errorMessage(e)),
  });
  const manual = MANUAL.some((m) => m.value === item.type);
  const meta = item.metadata as { toStageName?: string; fromStageName?: string; direction?: string; to?: string[] };
  return (
    <li className="group relative">
      <span className="absolute -left-[2.05rem] flex size-7 items-center justify-center rounded-full border bg-card text-muted-foreground [&_svg]:size-3.5" aria-hidden>
        {ICONS[item.type]}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-medium">{item.subject || MANUAL.find((m) => m.value === item.type)?.label || item.type}</span>
        {item.durationMinutes ? <span className="text-xs text-muted-foreground">{item.durationMinutes} min</span> : null}
        {meta.direction === 'outbound' && meta.to?.length ? <span className="text-xs text-muted-foreground">to {meta.to.join(', ')}</span> : null}
      </div>
      <p className="text-xs text-muted-foreground">
        {item.actorKind === 'client' ? `${item.actorName ?? 'Client'} (client)` : (item.actorName ?? 'System')} ·{' '}
        <time dateTime={new Date(item.occurredAt).toISOString()} title={formatDateTime(item.occurredAt)}>
          {formatRelative(item.occurredAt)}
        </time>
      </p>
      <ActivityBody item={item} />
      {manual ? (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 size-7 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          aria-label="Remove activity"
          onClick={() => setConfirm(true)}
        >
          <Trash2 />
        </Button>
      ) : null}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Remove this activity?" destructive confirmLabel="Remove" loading={archive.isPending} onConfirm={() => archive.mutate({ id: item.id })} />
    </li>
  );
}

function ActivityComposer({ anchor }: { anchor: Anchor }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const [type, setType] = useState<ActivityType>('note');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [duration, setDuration] = useState('');
  const [when, setWhen] = useState('');
  const create = trpc.crm.activities.create.useMutation({
    onSuccess: async () => {
      setSubject('');
      setBody('');
      setDuration('');
      setWhen('');
      toast.success('Activity logged');
      // Logged calls, emails and notes reset a deal's rotting clock.
      await Promise.all([utils.crm.activities.list.invalidate(), anchor.dealId ? utils.crm.deals.invalidate() : null]);
    },
    onError: (e) => toast.error('Could not log activity', errorMessage(e)),
  });
  const submit = () => {
    if (!subject.trim() && !body.trim()) return;
    create.mutate({
      ...anchor,
      type,
      subject: subject.trim() || null,
      body: body.trim() || null,
      durationMinutes: duration ? Number(duration) : null,
      occurredAt: when ? new Date(when) : undefined,
      metadata: {},
    });
  };
  return (
    <Card className="space-y-3 p-4">
      <div role="radiogroup" aria-label="Activity type" className="flex flex-wrap gap-1">
        {MANUAL.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={type === m.value}
            onClick={() => setType(m.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs [&_svg]:size-3.5',
              type === m.value ? 'bg-accent text-accent-foreground' : 'bg-foreground/[0.04] text-muted-foreground hover:bg-muted',
            )}
          >
            {ICONS[m.value]}
            {m.label}
          </button>
        ))}
      </div>
      <Input aria-label="Subject" placeholder={type === 'note' ? 'Title (optional)' : 'Subject'} value={subject} onChange={(e) => setSubject(e.target.value)} />
      <Textarea aria-label="Details" rows={3} placeholder="What happened?" value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="flex flex-wrap items-end gap-3">
        {type === 'call' || type === 'meeting' || type === 'site_visit' ? (
          <Field label="Minutes" className="w-24">
            <Input type="number" min={0} max={1440} value={duration} onChange={(e) => setDuration(e.target.value)} />
          </Field>
        ) : null}
        <Field label="When" className="w-56">
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
        </Field>
        <Button className="ml-auto" loading={create.isPending} disabled={!subject.trim() && !body.trim()} onClick={submit}>
          Log {MANUAL.find((m) => m.value === type)?.label.toLowerCase()}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

type TaskItem = RouterOutputs['crm']['tasks']['list']['items'][number];

export function TaskRow({ task, showLinks = true }: { task: TaskItem; showLinks?: boolean }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const toggle = trpc.crm.tasks.toggle.useMutation({
    onSuccess: async (t) => {
      if (t.status === 'done') toast.success('Task completed');
      await Promise.all([utils.crm.tasks.invalidate(), utils.crm.activities.list.invalidate()]);
    },
    onError: (e) => toast.error('Could not update task', errorMessage(e)),
  });
  const overdue = task.dueAt && task.status !== 'done' && new Date(task.dueAt) < new Date();
  return (
    <li className="flex items-start gap-3 py-2.5">
      <Checkbox
        className="mt-0.5"
        checked={task.status === 'done'}
        disabled={toggle.isPending}
        aria-label={task.status === 'done' ? `Mark "${task.title}" as not done` : `Complete "${task.title}"`}
        onCheckedChange={() => toggle.mutate({ id: task.id })}
      />
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm', task.status === 'done' && 'text-muted-foreground line-through')}>{task.title}</p>
        <p className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
          {task.dueAt ? <span className={cn(overdue && 'font-medium text-destructive')}>Due {formatDate(task.dueAt)}</span> : <span>No due date</span>}
          {task.assigneeName ? <span>· {task.assigneeName}</span> : null}
          {showLinks && task.contactName ? <span>· {task.contactName}</span> : null}
          {showLinks && task.dealTitle ? <span>· {task.dealTitle}</span> : null}
          {showLinks && task.projectName ? <span>· {task.projectName}</span> : null}
        </p>
      </div>
      {task.priority === 'high' || task.priority === 'urgent' ? <Badge variant={task.priority === 'urgent' ? 'destructive' : 'warning'}>{task.priority === 'urgent' ? 'Urgent' : 'High'}</Badge> : null}
    </li>
  );
}

/** Inline "add a task" row. */
export function QuickTask({ anchor, defaultAssignee }: { anchor: Anchor; defaultAssignee?: string | null }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const members = useMembers();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [assignee, setAssignee] = useState<string | null | undefined>(defaultAssignee);
  const [priority, setPriority] = useState<'normal' | 'high'>('normal');
  const create = trpc.crm.tasks.create.useMutation({
    onSuccess: async () => {
      setTitle('');
      setDue('');
      toast.success('Task added');
      await utils.crm.tasks.invalidate();
      await utils.crm.deals.get.invalidate();
    },
    onError: (e) => toast.error('Could not add task', errorMessage(e)),
  });
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        create.mutate({
          ...anchor,
          title: title.trim(),
          dueAt: due ? new Date(`${due}T17:00:00`) : null,
          assigneeId: assignee === undefined ? undefined : assignee,
          priority,
          status: 'todo',
        });
      }}
    >
      <Field label="New task" className="min-w-48 flex-1">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Follow up on…" />
      </Field>
      <Field label="Due" className="w-40">
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
      </Field>
      <Field label="Assignee" className="w-44">
        <MemberSelect value={assignee ?? null} onChange={setAssignee} placeholder={members.isLoading ? 'Loading…' : 'Me'} />
      </Field>
      <label className="flex h-9 items-center gap-2 text-sm">
        <Checkbox checked={priority === 'high'} onCheckedChange={(v) => setPriority(v ? 'high' : 'normal')} /> High
      </label>
      <Button type="submit" loading={create.isPending} disabled={!title.trim()}>
        Add
      </Button>
    </form>
  );
}

/** Tasks linked to a record, with a quick-add row. */
export function LinkedTasks({ anchor }: { anchor: Anchor }) {
  const query = trpc.crm.tasks.list.useQuery({ ...anchor, limit: 100 });
  const open = (query.data?.items ?? []).filter((t) => t.status === 'todo' || t.status === 'in_progress');
  const done = (query.data?.items ?? []).filter((t) => t.status === 'done').slice(0, 5);
  return (
    <div className="space-y-3">
      <QuickTask anchor={anchor} />
      {query.isLoading ? (
        <Skeleton className="h-20" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : open.length + done.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tasks yet.</p>
      ) : (
        <ul className="divide-y">
          {[...open, ...done].map((t) => (
            <TaskRow key={t.id} task={t} showLinks={false} />
          ))}
        </ul>
      )}
    </div>
  );
}
